import { useEntryCRUDActions } from '@mm/state/actions/EntryCRUDActions';
import WidgetModel, { ListAndGalleryEntry } from '@mm/state/model/WidgetModel';
import { get } from 'lodash';
import React, { useState } from 'react';
import { ErrorTooltip } from '../../ErrorTooltip/ErrorTooltip';
import Spinner from '../../Spinner';
import TextInput from '../../TextInput';

type UpdateEntryType = ReturnType<typeof useEntryCRUDActions>['update'];

type onRemoteWidgetUpdatedType = () => void;

type Props = {
  widget: WidgetModel;
  onRemoteWidgetUpdated: onRemoteWidgetUpdatedType;
  onImageTitleUpdated?: (value: string) => void;
};

const updateEntryHelper = async (
  widget: WidgetModel,
  fieldKey: 'title' | 'description',
  fieldValue: string,
  updateEntry: UpdateEntryType,
  onRemoteWidgetUpdated: onRemoteWidgetUpdatedType,
): Promise<void> => {
  const entry: ListAndGalleryEntry | undefined = get(
    widget,
    'config.entries[0]',
  );
  if (entry) {
    await updateEntry(widget.id, {
      ...entry,
      [fieldKey]: fieldValue,
    });
  }
  onRemoteWidgetUpdated();
};

const titleValidation = 'Title can not be blank';

export const OptinGalleryTextFields = (props: Props) => {
  const { widget, onRemoteWidgetUpdated, onImageTitleUpdated } = props;
  const [updating, setIsUpdating] = useState('');
  const [title, setTitle] = useState(
    get(widget, 'config.entries[0].title') || '',
  );
  const [subtitle, setSubtitle] = useState(
    get(widget, 'config.entries[0].description') || '',
  );
  const titleValid = title !== '';

  const { update: updateEntry } = useEntryCRUDActions();

  const onChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTitle(value);
    onImageTitleUpdated?.(value);
  };
  const onChangeSubtitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSubtitle(value);
  };

  return (
    <>
      <div className="optin-page-builder__inputs-container">
        <div className="optin-page-builder__input-container">
          <div className="optin-page-builder__field-title optin-page-builder__image-title-field-title">
            Image Title
          </div>
          <div className="optin-page-builder__text-input-container">
            <TextInput
              className="optin-page-builder__input"
              placeholder="Enter a title for your image"
              value={title}
              disabled={Boolean(updating)}
              onChange={onChangeTitle}
              onBlur={async () => {
                if (!titleValid) {
                  return;
                }
                setIsUpdating('title');
                try {
                  await updateEntryHelper(
                    widget,
                    'title',
                    title,
                    updateEntry,
                    onRemoteWidgetUpdated,
                  );
                  setIsUpdating('');
                } catch (error) {
                  setIsUpdating('');
                }
              }}
            />
            {updating === 'title' && (
              <div className="optin-page-builder__input-spinner-wrapper">
                <Spinner
                  show={true}
                  position="absolute"
                  backgroundColor="none"
                  size={15}
                />
              </div>
            )}
            {!titleValid && (
              <ErrorTooltip
                classPrefix={'optin-page-builder'}
                validation={[titleValidation]}
              />
            )}
          </div>
        </div>
      </div>
      <div className="optin-page-builder__inputs-container">
        <div className="optin-page-builder__input-container">
          <div className="optin-page-builder__field-title">
            Image Subtitle (Optional)
          </div>
          <div className="optin-page-builder__text-input-container">
            <TextInput
              className="optin-page-builder__input"
              placeholder="Enter a subtitle to provide more information"
              value={subtitle}
              disabled={Boolean(updating)}
              onChange={onChangeSubtitle}
              onBlur={async () => {
                setIsUpdating('subtitle');
                try {
                  await updateEntryHelper(
                    widget,
                    'description',
                    subtitle,
                    updateEntry,
                    onRemoteWidgetUpdated,
                  );
                  setIsUpdating('');
                } catch (error) {
                  setIsUpdating('');
                }
              }}
            />
            {updating === 'subtitle' && (
              <div className="optin-page-builder__input-spinner-wrapper">
                <Spinner
                  show={true}
                  position="absolute"
                  backgroundColor="none"
                  size={15}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
